import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { BlueColorStart500 } from "../../../constants/constants";
import { CountryCode } from "../../../constants/countryCurrency";
import { useSelector } from "react-redux";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense("AM5C258896422");

const BarChart = ({
  chartId,
  chartClass,
  showLegend = true,
  showLabels = true,
  chartData,
  chartLabels,
  chartColorSet,
  minimumGridDistance,
  isHorizontal = true,
  cellStartLocation = 0.4,
  cellEndLocation = 0.6,
  variant,
  showMatrixLegend
}) => {
  const userData = useSelector((state) => state.userData.user[0]);
  const userCountryCode = userData?.country_code ?? "IN";
  let country = {};
  CountryCode.forEach((data) => {
    if (data.countryCode === userCountryCode) country = data;
  });

  const transformData = (data) => data.map(item => {
    const newItem = { ...item };
    Object.keys(newItem).forEach(key => {
      if (typeof newItem[key] === 'string') {
        const num = parseFloat(newItem[key]);
        if (!isNaN(num)) newItem[key] = num;
      }
    });
    return newItem;
  });

  useLayoutEffect(() => {
    const root = am5.Root.new(chartId);
    const colorSet = chartColorSet || BlueColorStart500;
    root.setThemes([am5themes_Animated.new(root)]);
    root.interfaceColors.set("grid", am5.color(0xffffff));

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingRight: 0,
        paddingTop: 5,
        paddingBottom: 0,
        paddingLeft: 0,
        maxTooltipDistance: -1,
      })
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomXY",
      })
    );
    cursor.lineY.set("visible", false);
    cursor.lineX.set("visible", false);

    const transformedData = transformData(chartData);

    let dataMin = 0;
    let dataMax = 0;
    if (!isHorizontal && Array.isArray(chartData) && chartLabels?.length > 0) {
      const keys = chartLabels.map(l => l.value);
      const values = chartData.flatMap(item => keys.map(key => Number(item[key]) || 0));
      dataMin = Math.min(0, ...values);
      dataMax = Math.max(0, ...values);
      if (dataMin === 0 && dataMax === 0) {
        dataMax = 1;
      }
    }

    let xAxis, yAxis;
    if (isHorizontal) {
      xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {
            stroke: am5.color(0x707070),
            strokeWidth: 1,
            strokeOpacity: 1,
          }),
          min: 0,
          extraMax: 0.1,
          strictMinMaxSelection: false,
          calculateTotals: true,
        })
      );
      // Set number formatter for X-axis to use K/M/B abbreviations
      xAxis.set("numberFormatter", am5.NumberFormatter.new(root, {
        numberFormat: "#,##a",
        bigNumberPrefixes: [
          { number: 1e3, suffix: "K" },
          { number: 1e6, suffix: "M" },
          { number: 1e9, suffix: "B" }
        ],
        smallNumberPrefixes: []
      }));
      // xAxis.get("renderer").labels.template.setAll({
      //   maxWidth: 150,
      //   fontSize: 12,
      //   fontFamily: "Ubuntu",
      //    fill: am5.color(0x757575),
      //   fontWeight: "400",
      //   visible: showLabels,
      //   textAlign: "center",
      // });
      xAxis.get("renderer").labels.template.setAll({
        fontSize: 12,
        fontFamily: "Ubuntu",
        fill: am5.color(0x757575),
        fontWeight: "400",
        visible: true,
        rotation: 0,
        textAlign: "center",
        dx: 0,
        dy: 10,
        oversizedBehavior: "truncate",
        maxWidth: 150,
      });
      xAxis.get("renderer").set("minGridDistance", 40);

      const yRenderer = am5xy.AxisRendererY.new(root, {
        stroke: am5.color(0x707070), // axis line color
        strokeWidth: 1,
        strokeOpacity: 1,
        cellStartLocation: cellStartLocation,
        cellEndLocation: cellEndLocation,
        minorGridEnabled: true,
      });
      if (minimumGridDistance !== undefined) {
        yRenderer.set("minGridDistance", minimumGridDistance);
      }
      yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "label",
          renderer: yRenderer,
          startLocation: 0.2,
          endLocation: 0.8,
        })
      );
      yAxis.get("renderer").labels.template.setAll({
        maxWidth: 150,
        fontSize: 12,
        fontFamily: "Ubuntu",
        fill: am5.color(0x757575),
        fontWeight: "400",
        visible: showLabels,
        textAlign: "right",
      });
      yAxis.data.setAll(transformedData);

      // Force show all category labels, prevent skipping
      yAxis.get("renderer").labels.template.set("forceHidden", false);
      yAxis.get("renderer").set("minGridDistance", 1);
      if (yAxis.get("renderer").set("autoHide", false)) {
        yAxis.get("renderer").set("autoHide", false);
      }

      // Adapter for truncating Y-axis labels (horizontal)
      yAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem;
        if (dataItem && dataItem.dataContext && dataItem.dataContext.label) {
          const label = dataItem.dataContext.label;
          return label.length > 8 ? label.slice(0, 8) + "..." : label;
        }
        return text;
      });
    } else {
      const xRenderer = am5xy.AxisRendererX.new(root, {
        stroke: am5.color(0x707070), // axis line color
        strokeWidth: 0,
        strokeOpacity: 0,
        cellStartLocation: cellStartLocation,
        cellEndLocation: cellEndLocation,
        minorGridEnabled: true,
      });
      xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "label",
          startLocation: 0.2,
          endLocation: 0.8,
          renderer: xRenderer,
        })
      );
      // xAxis.get("renderer").labels.template.setAll({
      //   maxWidth: 150,
      //   fontSize: 12,
      //   fontFamily: "Ubuntu",
      //  fill: am5.color(0x757575),
      //   fontWeight: "400",
      //   visible: showLabels,
      //   rotation: 315,
      //   textAlign: "left",
      // });
      xAxis.get("renderer").labels.template.setAll({
        fontSize: 12,
        fontFamily: "Ubuntu",
        fill: am5.color(0x757575),
        fontWeight: "400",
        visible: showLabels,
        rotation: 0, // <-- this rotates labels!
        textAlign: "left",
        dx: 0,
        dy: 10,
        oversizedBehavior: "truncate",
        maxWidth: 150,
      });
      xAxis.data.setAll(transformedData);

      // Adapter for truncating X-axis labels (vertical)
      xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem;
        if (dataItem && dataItem.dataContext && dataItem.dataContext.label) {
          const label = dataItem.dataContext.label;
          return label.length > 8 ? label.slice(0, 8) + "..." : label;
        }
        return text;
      });

      const yRenderer = am5xy.AxisRendererY.new(root, {
        stroke: am5.color(0x707070),
        strokeWidth: 1,
        strokeOpacity: 1,
      });
      yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          min: Math.min(dataMin, 0),
          max: Math.max(dataMax, 0),
          baseValue: 0,
          strictMinMax: false,
          calculateTotals: true,
          numberFormat: "#,##0.##",
        })
      );
      yAxis.get("renderer").labels.template.setAll({
        maxWidth: 150,
        fontSize: 12,
        fontFamily: "Ubuntu",
        fill: am5.color(0x757575),
        fontWeight: "400",
        visible: showLabels,
      });

      yAxis.get("renderer").grid.template.setAll({
        visible: false
      });

      let range = yAxis.createAxisRange(yAxis.makeDataItem({ value: 0 }));
      range.get("grid").setAll({
        visible: true,
        stroke: am5.color(0x707070),
        // strokeWidth: 1,
        // strokeOpacity: 0.7
        strokeWidth: 1,              // thicker for visibility
        strokeOpacity: 1
      });
    }

    const allSeries = [];

    chartLabels.forEach((label, index) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: label.label,
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: isHorizontal ? label.value : undefined,
          valueYField: isHorizontal ? undefined : label.value,
          categoryXField: isHorizontal ? undefined : "label",
          categoryYField: isHorizontal ? "label" : undefined,
          fill: am5.color(colorSet[index]),
          stroke: am5.color(colorSet[index]),
        })
      );

      series.columns.template.setAll({
        ...(isHorizontal
          ? {
            cornerRadiusTR: 7,
            cornerRadiusBR: 7,
            height: 10,
          }
          : {
            cornerRadiusTL: 7,
            cornerRadiusTR: 7,
            width: 10,
          })
      });

      const styledTooltip = am5.Tooltip.new(root, {
        pointerOrientation: "horizontal",
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        autoTextColor: false
      });

      styledTooltip.label.setAll({
        fill: am5.color(0xffffff),
        fontWeight: "bold",
        fontSize: 12,
        maxWidth: 300,
        oversizedBehavior: "wrap",
        fontFamily: "Ubuntu",
      });

      styledTooltip.get("background").setAll({
        fill: am5.color(0x213345),
        stroke: am5.color(0x213345),
        strokeWidth: 1,
        cornerRadius: 5
      });

      series.columns.template.set("tooltip", styledTooltip);

      allSeries.push({
        series,
        label,
        color: colorSet[index]
      });

      series.data.setAll(transformedData);
      series.appear(1000);
    });

    allSeries.forEach((s, idx) => {
      s.series.columns.template.adapters.add("tooltipText", (text, target) => {
        const ctx = target.dataItem?.dataContext;
        if (!ctx) return "";
        let output = `[font-size:12px][#ffffff][/]  [bold]${ctx.label}[/]\n`;
        allSeries.forEach((s2) => {
          let val = ctx[s2.label.value];
          if (val !== undefined && val !== null && val !== "") {
            let formattedVal = formatNumberForTooltip(val, s2.label.label);
            output += `[font-size:12px][#${am5.color(s2.color).toCSSHex().replace("#", "")}]●[/] ${s2.label.label}: [bold]${formattedVal}[/]\n`;
          }
        });
        return output.trim();
      });
    });

    // Centered legend logic
    //     if (showLegend) {
    //       let legend;
    //       legend = am5.Legend.new(root, {
    //         centerX: am5.percent(50),
    //         x: am5.percent(50),
    //         marginTop: 10,
    //         marginBottom: 0,
    //         useDefaultMarker: true,
    //         layout: am5.GridLayout.new(root, {
    //           maxColumns: chartLabels.length || 5,
    //           fixedWidthGrid: false,
    //           justifyContent: "center",
    //           alignItems: "center",
    //         }),
    //         paddingLeft: 0,
    //         paddingRight: 0,
    //         paddingTop: 0,
    //         paddingBottom: 0,
    //       });

    //       legend.labels.template.setAll({
    //         fontSize: 14,
    //         // fontWeight: "400",
    //         fill: am5.color(0x232b34),
    //         marginLeft: 8,
    //         marginRight: 0,
    //         textAlign: "center",
    //         centerY: am5.p50,
    //         fontFamily: "Ubuntu"
    //       });

    //    legend.markers.template.setAll({
    //   width: 12,
    //   height: 12,
    //   centerY: am5.p50,
    //   centerX: am5.p50,
    //   strokeWidth: 2,
    //   fillOpacity: 1,
    //   strokeOpacity: 1,
    //   radius: 6 // Half the width/height for a perfect circle
    // });
    // legend.markerRectangles.template.setAll({
    //   cornerRadiusTL: 12,
    //   cornerRadiusTR: 12,
    //   cornerRadiusBL: 12,
    //   cornerRadiusBR: 12,
    //   width: 12,
    //   height: 12
    // });

    //       legend.markers.template.adapters.add("stroke", (stroke, target) => {
    //         const series = target.dataItem?.dataContext;
    //         if (series) {
    //           return series.get("fill");
    //         }
    //         return stroke;
    //       });

    //       legend.set("background", undefined);

    //       legend.itemContainers.template.setAll({
    //         paddingTop: 0,
    //         paddingBottom: 0,
    //         paddingLeft: 0,
    //         paddingRight: 24,
    //         minWidth: 0,
    //         minHeight: 0,
    //         centerY: am5.p50,
    //       });

    //       chart.bottomAxesContainer.children.push(legend);
    //       legend.data.setAll(chart.series.values);
    //     }
    if (showLegend) {
      let legend;
      legend = am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 10,
        marginBottom: 0,
        useDefaultMarker: true,
        layout: am5.GridLayout.new(root, {
          maxColumns: chartLabels.length || 5,
          fixedWidthGrid: false,
          justifyContent: "center",
          alignItems: "center",
        }),
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      });

      legend.labels.template.setAll({
        fontFamily: "Ubuntu, sans-serif",
        fontSize: 12,
        fill: am5.color(0x213345), // navy blue
        fontWeight: "400",
        oversizedBehavior: "wrap",
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 5,
        paddingRight: 5,
        textAlign: "center",
      });

      legend.markers.template.setAll({
        width: 12,
        height: 12,
        strokeWidth: 2,
        fillOpacity: 1,
        strokeOpacity: 1,
        centerY: am5.p50,
        centerX: am5.p50,
      });
      legend.markerRectangles.template.setAll({
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        cornerRadiusBL: 2,
        cornerRadiusBR: 2,
        width: 12,
        height: 12,
      });

      legend.markers.template.adapters.add("stroke", (stroke, target) => {
        const series = target.dataItem?.dataContext;
        if (series) {
          return series.get("fill");
        }
        return stroke;
      });

      legend.set("background", undefined);

      legend.itemContainers.template.setAll({
        layout: root.horizontalLayout,
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 5,
        paddingRight: 5,
        minWidth: 0,
        minHeight: 0,
        centerY: am5.p50,
        display: "flex"
      });

      chart.bottomAxesContainer.children.push(legend);
      legend.data.setAll(chart.series.values);
    }

    chart.zoomOutButton.set("forceHidden", true);
    chart.appear(1000, 100);

    return () => { root.dispose(); };
  }, [
    chartId,
    chartClass,
    showLegend,
    showLabels,
    chartData,
    chartLabels,
    chartColorSet,
    minimumGridDistance,
    isHorizontal,
    cellStartLocation,
    cellEndLocation,
    variant,
    showMatrixLegend
  ]);

  const height = variant === "centeredLegend" || showMatrixLegend ? "100%" : "230px";

  return (
    <Wrapper>
      <div id={chartId} className={chartClass} style={{ height, minHeight: height, width: "100%" }}></div>
    </Wrapper>
  );
};

BarChart.propTypes = {
  chartId: PropTypes.string.isRequired,
  chartClass: PropTypes.string,
  showLegend: PropTypes.bool,
  showLabels: PropTypes.bool,
  chartData: PropTypes.array.isRequired,
  chartLabels: PropTypes.array.isRequired,
  chartColorSet: PropTypes.array,
  minimumGridDistance: PropTypes.number,
  isHorizontal: PropTypes.bool,
  cellStartLocation: PropTypes.number,
  cellEndLocation: PropTypes.number,
  variant: PropTypes.string,
  showMatrixLegend: PropTypes.bool
};

export default BarChart;